import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import { CheckCircle2, ArrowRight, ArrowLeft, Calendar as CalendarIcon } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { BOOKING as T } from "@/constants/testIds";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";

const VEHICLE_TYPES = [
  { id: "small", label: "Small / Hatch" },
  { id: "medium", label: "Medium / Sedan" },
  { id: "large", label: "Large / SUV / 4WD" },
];

const TIME_SLOTS = [
  "08:30",
  "10:00",
  "11:30",
  "13:00",
  "14:30",
  "16:00",
];

const stepLabels = ["Service", "Vehicle", "Schedule", "Contact"];

export default function Booking({ services, initialService }) {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);
  const [blockedByDate, setBlockedByDate] = useState({}); // { "YYYY-MM-DD": [] | ["HH:MM", ...] }
  const [form, setForm] = useState({
    service: initialService || "",
    vehicle_type: "",
    vehicle_make_model: "",
    preferred_date: "",
    preferred_time: "",
    add_engine_bay: false,
    name: "",
    email: "",
    phone: "",
    notes: "",
    payment_method: "stripe",
  });

  useEffect(() => {
    api
      .get("/availability")
      .then((r) => {
        // Merge admin blackouts + already-booked slots into a single map so
        // the calendar + time buttons only need one source of truth.
        // Semantics:
        //   map[date] === []            → whole day blocked
        //   map[date] === ["HH:MM", …]  → those slots blocked
        //   date missing                → fully available
        const map = {};
        const mark = (date, slots) => {
          if (!date) return;
          if (map[date] === undefined) {
            map[date] = Array.isArray(slots) ? [...slots] : [];
          } else if (map[date].length === 0 || !slots || slots.length === 0) {
            // If either side is a whole-day block, keep it as whole-day.
            map[date] = [];
          } else {
            map[date] = Array.from(new Set([...map[date], ...slots]));
          }
        };
        (r.data.blocks || []).forEach((b) => mark(b.date, b.slots || []));
        (r.data.booked || []).forEach((b) => mark(b.date, b.slots || []));
        setBlockedByDate(map);
      })
      .catch(() => setBlockedByDate({}));
  }, []);

  useEffect(() => {
    if (initialService && initialService !== form.service) {
      setForm((f) => ({ ...f, service: initialService }));
      setStep(1);
      // scroll into view
      const el = document.getElementById("booking");
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
    // eslint-disable-next-line
  }, [initialService]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canProceed = () => {
    if (step === 0) return !!form.service;
    if (step === 1) return !!form.vehicle_type;
    if (step === 2) return !!form.preferred_date && !!form.preferred_time;
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const res = await api.post("/bookings", {
        ...form,
        origin_url: window.location.origin,
      });
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
        return;
      }
      setSuccess(res.data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const svc = services.find((s) => s.id === form.service);
  const totalPrice = (svc?.price || 0) + (form.add_engine_bay ? 100 : 0);

  return (
    <section id="booking" data-testid={T.root} className="py-28 md:py-40 bg-[#050505]">
      <div className="max-w-[1200px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16 md:mb-20">
          <p className="md:col-span-5 eyebrow">Reserve — Step {step + 1} / 4</p>
          <div className="md:col-span-7">
            <h2 className="font-serif text-5xl md:text-7xl font-light text-white leading-[0.95]">
              Book your <span className="italic text-neutral-400">detail.</span>
            </h2>
            <p className="mt-6 text-neutral-400 max-w-lg text-sm md:text-base leading-relaxed">
              A <span className="text-brand-red">$50 deposit</span> secures your slot,
              paid securely via Stripe. Full balance is due on completion.
            </p>
          </div>
        </div>

        {success ? (
          <SuccessCard data={success} form={form} svc={svc} total={totalPrice} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            {/* Steps rail */}
            <ol className="md:col-span-3 space-y-4">
              {stepLabels.map((label, i) => (
                <li key={label} className="flex items-center gap-3">
                  <span
                    className={`font-mono text-[11px] tracking-[0.24em] transition-colors ${
                      i === step
                        ? "text-white"
                        : i < step
                          ? "text-neutral-400"
                          : "text-neutral-600"
                    }`}
                  >
                    0{i + 1}
                  </span>
                  <span
                    className={`font-serif text-xl transition-colors ${
                      i === step
                        ? "text-white"
                        : i < step
                          ? "text-neutral-400 line-through decoration-1"
                          : "text-neutral-600"
                    }`}
                  >
                    {label}
                  </span>
                </li>
              ))}
            </ol>

            {/* Form panel */}
            <div className="md:col-span-9 border-t border-white/10 pt-10">
              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  data-testid={T.step(step)}
                >
                  {step === 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {services.map((s) => (
                        <button
                          key={s.id}
                          onClick={() => set("service", s.id)}
                          data-testid={T.serviceSelect(s.id)}
                          className={`text-left p-6 border transition-all duration-300 ${
                            form.service === s.id
                              ? "border-white bg-white/[0.04]"
                              : "border-white/10 hover:border-white/30"
                          }`}
                        >
                          <div className="flex items-baseline justify-between mb-3">
                            <span className="font-serif text-2xl md:text-3xl text-white leading-tight">
                              {s.name}
                            </span>
                            <span className="font-serif text-3xl text-white">
                              ${s.price}
                            </span>
                          </div>
                          <p className="font-mono text-[10px] tracking-[0.24em] uppercase text-neutral-500">
                            {s.subtitle}
                          </p>
                        </button>
                      ))}
                    </div>
                  )}

                  {step === 1 && (
                    <div className="space-y-8">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {VEHICLE_TYPES.map((v) => (
                          <button
                            key={v.id}
                            onClick={() => set("vehicle_type", v.id)}
                            data-testid={T.vehicleType(v.id)}
                            className={`p-5 border font-serif text-xl text-left transition-all duration-300 ${
                              form.vehicle_type === v.id
                                ? "border-white bg-white/[0.04] text-white"
                                : "border-white/10 hover:border-white/30 text-neutral-300"
                            }`}
                          >
                            {v.label}
                          </button>
                        ))}
                      </div>
                      <div>
                        <label className="eyebrow block mb-3">
                          Make & Model (optional)
                        </label>
                        <input
                          data-testid={T.makeModel}
                          className="input-minimal"
                          placeholder="e.g. Ford Mustang GT"
                          value={form.vehicle_make_model}
                          onChange={(e) => set("vehicle_make_model", e.target.value)}
                        />
                      </div>
                      {svc && svc.id !== "ceramic-coating" && (
                        <label className="flex items-start gap-4 cursor-pointer group py-3 border-t border-white/10">
                          <input
                            type="checkbox"
                            data-testid={T.engineBay}
                            checked={form.add_engine_bay}
                            onChange={(e) => set("add_engine_bay", e.target.checked)}
                            className="mt-1 accent-white w-4 h-4"
                          />
                          <span>
                            <span className="block font-serif text-xl text-white">
                              Add engine bay clean
                            </span>
                            <span className="block text-neutral-500 text-sm">
                              +$100 · full-detail bays only
                            </span>
                          </span>
                        </label>
                      )}
                    </div>
                  )}

                  {step === 2 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                      <div>
                        <label className="eyebrow block mb-3">Preferred Date</label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <button
                              type="button"
                              data-testid={T.date}
                              className="input-minimal text-left flex items-center justify-between hover:border-white/40 transition-colors"
                            >
                              <span className={form.preferred_date ? "text-white" : "text-white/35"}>
                                {form.preferred_date
                                  ? new Date(form.preferred_date + "T00:00:00").toLocaleDateString(
                                      undefined,
                                      { weekday: "short", day: "numeric", month: "long", year: "numeric" }
                                    )
                                  : "Select a date"}
                              </span>
                              <CalendarIcon size={16} className="text-white/50" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent
                            align="start"
                            className="w-auto p-0 bg-[#0a0a0a] border border-white/10 text-white"
                            data-lenis-prevent
                          >
                            <Calendar
                              mode="single"
                              selected={
                                form.preferred_date
                                  ? new Date(form.preferred_date + "T00:00:00")
                                  : undefined
                              }
                              onSelect={(d) => {
                                if (!d) return;
                                const yyyy = d.getFullYear();
                                const mm = String(d.getMonth() + 1).padStart(2, "0");
                                const dd = String(d.getDate()).padStart(2, "0");
                                const key = `${yyyy}-${mm}-${dd}`;
                                set("preferred_date", key);
                                // Reset selected time if it's now blocked
                                const blocks = blockedByDate[key];
                                if (blocks && (blocks.length === 0 || blocks.includes(form.preferred_time))) {
                                  set("preferred_time", "");
                                }
                              }}
                              disabled={(d) => {
                                const today = new Date();
                                today.setHours(0, 0, 0, 0);
                                if (d < today) return true;
                                const yyyy = d.getFullYear();
                                const mm = String(d.getMonth() + 1).padStart(2, "0");
                                const dd = String(d.getDate()).padStart(2, "0");
                                const key = `${yyyy}-${mm}-${dd}`;
                                const blk = blockedByDate[key];
                                if (blk === undefined) return false;
                                // Whole-day admin block
                                if (blk.length === 0) return true;
                                // Every possible slot is already taken → disable the day
                                return TIME_SLOTS.every((s) => blk.includes(s));
                              }}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div>
                        <label className="eyebrow block mb-3">Preferred Time</label>
                        <div className="grid grid-cols-3 gap-2" data-testid={T.time}>
                          {TIME_SLOTS.map((slot) => {
                            const blockedSlots = blockedByDate[form.preferred_date];
                            const bookingLocked =
                              blockedSlots !== undefined &&
                              (blockedSlots.length === 0 || blockedSlots.includes(slot));
                            // Hide/disable slots earlier than "now + 60min buffer"
                            // when the selected date is today.
                            let inPast = false;
                            if (form.preferred_date) {
                              const [hh, mm] = slot.split(":").map(Number);
                              const slotDate = new Date(form.preferred_date + "T00:00:00");
                              slotDate.setHours(hh, mm, 0, 0);
                              const now = new Date();
                              inPast = slotDate.getTime() - now.getTime() < 60 * 60 * 1000;
                            }
                            const isBlocked = bookingLocked || inPast;
                            const label = bookingLocked
                              ? "Booked"
                              : inPast
                              ? "Past"
                              : null;
                            return (
                              <button
                                key={slot}
                                type="button"
                                onClick={() => !isBlocked && set("preferred_time", slot)}
                                disabled={isBlocked}
                                data-testid={`booking-time-${slot}`}
                                title={
                                  bookingLocked
                                    ? "Already booked"
                                    : inPast
                                    ? "Too close to now"
                                    : ""
                                }
                                className={`relative py-3 border font-mono text-xs transition-all ${
                                  isBlocked
                                    ? "border-white/5 text-neutral-700 cursor-not-allowed"
                                    : form.preferred_time === slot
                                    ? "border-white bg-white text-black"
                                    : "border-white/10 hover:border-white/30 text-neutral-300"
                                }`}
                              >
                                <span className={isBlocked ? "line-through" : ""}>
                                  {slot}
                                </span>
                                {label && (
                                  <span className="block mt-0.5 text-[9px] tracking-widest uppercase text-neutral-600 no-underline">
                                    {label}
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}

                  {step === 3 && (
                    <div className="space-y-8">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                        <div>
                          <label className="eyebrow block mb-3">Full Name</label>
                          <input
                            data-testid={T.name}
                            className="input-minimal"
                            value={form.name}
                            onChange={(e) => set("name", e.target.value)}
                            required
                          />
                        </div>
                        <div>
                          <label className="eyebrow block mb-3">Phone</label>
                          <input
                            data-testid={T.phone}
                            className="input-minimal"
                            value={form.phone}
                            onChange={(e) => set("phone", e.target.value)}
                            required
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="eyebrow block mb-3">Email</label>
                          <input
                            data-testid={T.email}
                            type="email"
                            className="input-minimal"
                            value={form.email}
                            onChange={(e) => set("email", e.target.value)}
                            required
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="eyebrow block mb-3">
                            Notes (optional)
                          </label>
                          <input
                            data-testid={T.notes}
                            className="input-minimal"
                            placeholder="Anything we should know?"
                            value={form.notes}
                            onChange={(e) => set("notes", e.target.value)}
                          />
                        </div>
                      </div>

                      {/* Payment method selector */}
                      <div>
                        <label className="eyebrow block mb-3">Pay Deposit With</label>
                        <div className="grid grid-cols-2 gap-2">
                          <button
                            type="button"
                            data-testid="booking-pay-stripe"
                            onClick={() => set("payment_method", "stripe")}
                            className={`py-4 border font-mono text-[11px] tracking-widest uppercase transition-all ${
                              form.payment_method === "stripe"
                                ? "border-white bg-white text-black"
                                : "border-white/10 hover:border-white/30 text-neutral-300"
                            }`}
                          >
                            Card · Stripe
                          </button>
                          <button
                            type="button"
                            data-testid="booking-pay-paypal"
                            onClick={() => set("payment_method", "paypal")}
                            className={`py-4 border font-mono text-[11px] tracking-widest uppercase transition-all ${
                              form.payment_method === "paypal"
                                ? "border-white bg-white text-black"
                                : "border-white/10 hover:border-white/30 text-neutral-300"
                            }`}
                          >
                            PayPal
                          </button>
                        </div>
                      </div>

                      {/* Summary */}
                      <div className="border-t border-white/10 pt-6 grid grid-cols-2 gap-y-3 text-sm">
                        <span className="text-neutral-500">Service</span>
                        <span className="text-white text-right">{svc?.name || "—"}</span>
                        <span className="text-neutral-500">Vehicle</span>
                        <span className="text-white text-right">
                          {VEHICLE_TYPES.find((v) => v.id === form.vehicle_type)?.label || "—"}
                        </span>
                        <span className="text-neutral-500">When</span>
                        <span className="text-white text-right">
                          {form.preferred_date} · {form.preferred_time}
                        </span>
                        {form.add_engine_bay && (
                          <>
                            <span className="text-neutral-500">Engine Bay Add-On</span>
                            <span className="text-white text-right">+$100</span>
                          </>
                        )}
                        <span className="text-neutral-500 font-mono text-[11px] tracking-[0.24em] uppercase pt-4 border-t border-white/10">
                          Estimate
                        </span>
                        <span className="text-white text-right font-serif text-3xl pt-4 border-t border-white/10">
                          ${totalPrice}
                        </span>
                        <span className="text-neutral-500 font-mono text-[11px] tracking-[0.24em] uppercase">
                          Deposit today
                        </span>
                        <span className="text-brand-red text-right font-serif text-3xl">
                          $50
                        </span>
                        <span className="text-neutral-500 font-mono text-[11px] tracking-[0.24em] uppercase pb-2">
                          Balance on completion
                        </span>
                        <span className="text-white text-right font-serif text-xl pb-2">
                          ${totalPrice - 50}
                        </span>
                      </div>

                      {/* Booking policy notice */}
                      <div
                        data-testid="booking-policy-notice"
                        className="mt-6 border border-white/10 bg-white/[0.02] p-5 md:p-6"
                      >
                        <p className="eyebrow mb-3">Booking Policy</p>
                        <p className="text-neutral-300 text-sm leading-relaxed">
                          A <span className="text-brand-red">$50 deposit</span> is required
                          when booking online. If you don't show up within 1 hour of your
                          appointment (without prior notice of delay over an hour or
                          cancellation after the agreed time), the deposit will be{" "}
                          <span className="text-white">non-refundable</span>.
                        </p>
                      </div>

                      {error && (
                        <p className="text-red-400 text-sm font-mono tracking-wide">
                          {error}
                        </p>
                      )}
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>

              {/* Nav buttons */}
              <div className="mt-12 flex items-center justify-between border-t border-white/10 pt-6">
                <button
                  onClick={() => setStep((s) => Math.max(0, s - 1))}
                  disabled={step === 0}
                  data-testid={T.back}
                  className={`flex items-center gap-2 font-mono text-[11px] tracking-[0.24em] uppercase transition-opacity ${
                    step === 0 ? "opacity-0 pointer-events-none" : "text-neutral-400 hover:text-white"
                  }`}
                >
                  <ArrowLeft size={14} /> Back
                </button>

                {step < 3 ? (
                  <button
                    onClick={() => setStep((s) => s + 1)}
                    disabled={!canProceed()}
                    data-testid={T.next}
                    className={`btn-primary ${!canProceed() ? "opacity-40 cursor-not-allowed" : ""}`}
                  >
                    Continue <ArrowRight size={14} />
                  </button>
                ) : (
                  <button
                    onClick={submit}
                    disabled={submitting}
                    data-testid={T.submit}
                    className="btn-primary"
                  >
                    {submitting
                      ? "Redirecting…"
                      : form.payment_method === "paypal"
                        ? "Pay $50 with PayPal"
                        : "Pay $50 Deposit & Book"}
                    <ArrowRight size={14} />
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function SuccessCard({ data, form, svc, total }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7 }}
      data-testid={T.success}
      className="border border-white/10 bg-white/[0.02] p-10 md:p-16"
    >
      <div className="flex items-start gap-6 mb-10">
        <CheckCircle2 className="text-white shrink-0" size={40} strokeWidth={1} />
        <div>
          <p className="eyebrow mb-3">Booking Received · #{data.id.slice(0, 8)}</p>
          <h3 className="font-serif text-4xl md:text-6xl text-white leading-tight">
            Thank you, <span className="italic text-neutral-400">{form.name.split(" ")[0]}.</span>
          </h3>
          <p className="mt-4 text-neutral-400 max-w-md">
            We'll confirm your appointment shortly by phone or email. In the meantime,
            here's a copy of your reservation.
          </p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-y-3 text-sm border-t border-white/10 pt-6">
        <span className="text-neutral-500">Service</span>
        <span className="text-white text-right">{svc?.name}</span>
        <span className="text-neutral-500">When</span>
        <span className="text-white text-right">
          {form.preferred_date} · {form.preferred_time}
        </span>
        <span className="text-neutral-500">Contact</span>
        <span className="text-white text-right">{form.phone}</span>
        <span className="text-neutral-500 font-mono text-[11px] tracking-[0.24em] uppercase pt-4 border-t border-white/10">
          Estimate
        </span>
        <span className="text-white text-right font-serif text-3xl pt-4 border-t border-white/10">
          ${total}
        </span>
      </div>
    </motion.div>
  );
}
